import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { Tooltip } from '@/components/ui/tooltip';
import { Link } from '@tanstack/react-router';
import { type PointerEvent, useState } from 'react';

/**
 * The PERSON'S NAME under the time of a line of Sessions (§9.2 and §12.1 of the
 * refonte, H75) - cut at twenty characters to hold in the line, and read WHOLE
 * in the bubble when it is cut (revue de !466).
 *
 * ## The bubble speaks only when the name overflows
 *
 * A bubble repeating a name already whole under the pointer is noise, so the
 * cut is MEASURED at the pointer's entry (`scrollWidth` past `clientWidth`) and
 * the bubble is handed a label only then - the `Tooltip` stays shut on an empty
 * label, and its own entry handler runs after this one, so the measure is in
 * before the bubble's delay runs out. A count of characters would have guessed:
 * twenty narrow letters fit where fifteen wide ones do not.
 *
 * ## A link to the session, like the pill beside it
 *
 * « La ligne ENTIÈRE ouvre la session »: the row's link lays a veil over the
 * line, so the name stands above it (`relative z-10`) to see the pointer, and is
 * itself the same link so a click still opens the session. It leaves the tab
 * order and the accessibility tree to the row's own link, and says the name in
 * words (`sr-only`) - the régime of `noyau-pill.tsx`, and a screen reader
 * reads the whole name, which only the eye ever lost.
 */

export interface PersonNameProps {
  readonly name: string;
  readonly sessionId: string;
}

export function PersonName({ name, sessionId }: PersonNameProps) {
  const [cut, setCut] = useState(false);
  const measure = (event: PointerEvent<HTMLElement>): void => {
    const shown = event.currentTarget;
    setCut(shown.scrollWidth > shown.clientWidth);
  };

  return (
    <>
      <Tooltip label={cut ? name : null} describes={false}>
        <Link
          to="/admin/sessions/$sessionId"
          params={{ sessionId }}
          tabIndex={-1}
          aria-hidden="true"
          data-testid={SESSIONS_TEST_IDS.rowPerson}
          onPointerEnter={measure}
          className="relative z-10 mt-0.5 block max-w-[20ch] truncate font-bold text-gray-900 text-xs"
        >
          {name}
        </Link>
      </Tooltip>
      <span className="sr-only">{name}</span>
    </>
  );
}
